import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Group } from '../../../../core/declarations/group.interface';
import { Settlement } from '../../../../core/declarations/settlement.interface';
import { UserState } from '../../../../core/states/user-state/user.reducers';
import { getCurrentUser } from '../../../../core/states/user-state/user.selectors';
import { GroupService } from '../../../services/group.service';
import { LoadingService } from '@bling-fe/shared/services';
import { SettlementService } from '../../../services/settlement.service';
import { marker as _ } from '@bling-fe/shared/utils';
import { select, Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { first, map, skipWhile, take } from 'rxjs/operators';
import { IonicModule } from '@ionic/angular';
import {
  ModalAbstractComponent,
  SelectModalBaseComponent
} from '@bling-fe/shared/modals/base-modal';
import { NgFor, NgIf } from '@angular/common';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { ArticleScope, ScopeOption } from '@bling-fe/shared/utils';
_('COMMON.POST_SCOPE.ALL');

@Component({
  selector: 'fl-select-post-scope-modal',
  templateUrl: './select-post-scope-modal.component.html',
  styleUrls: ['./select-post-scope-modal.component.scss'],
  standalone: true,
  imports: [
    IonicModule,
    TranslateModule,
    SelectModalBaseComponent,
    NgFor,
    NgIf,
    SvgIconComponent
  ],
  providers: [
    {
      provide: ModalAbstractComponent,
      useExisting: SelectPostScopeModalComponent
    }
  ]
})
export class SelectPostScopeModalComponent
  extends ModalAbstractComponent<{ value: ArticleScope }, ArticleScope>
  implements OnInit
{
  searchFilter = new BehaviorSubject<string>('');

  private groupService = inject(GroupService);
  private destroyRef = inject(DestroyRef);
  groups$!: Observable<Group[]>;
  settlements$!: Observable<Settlement[]>;

  // Displayed (filtered) lists; `all*` hold the full set + selection state.
  groups: ScopeOption[] = [];
  settlements: ScopeOption[] = [];
  private allGroups: ScopeOption[] = [];
  private allSettlements: ScopeOption[] = [];
  publishGlobally = true;

  private userStore = inject(Store<UserState>);
  private loading = inject(LoadingService);
  private settlementService = inject(SettlementService);

  staffOrAdmin$ = this.userStore.pipe(
    select(getCurrentUser),
    first(),
    map(u => u.super_admin || u.admin || u.staff)
  );

  ngOnInit(): void {
    this.loading.show();
    this.fetchSettlements();
    this.fetchGroups();
    this.loadOptions();
  }

  closeModal() {
    this.dismiss({
      groups: this.allGroups.filter(group => group.selected),
      settlements: this.allSettlements.filter(
        settlement => settlement.selected
      ),
      publish_globally: this.publishGlobally
    });
  }

  closeModalWithoutSaving() {
    this.dismiss(this.props.value);
  }

  private loadOptions() {
    // Build the full lists once (with their selected state). The displayed
    // lists are a filtered view recomputed on every search term, so selections
    // survive filtering and the search actually reacts to typing.
    combineLatest([this.settlements$, this.groups$])
      .pipe(take(1))
      .subscribe(([settlements, groups]) => {
        this.allSettlements = settlements.map(settlement => ({
          ...settlement,
          selected: !!this.props.value.settlements.find(
            s => s.id === (settlement.id as number)
          )
        }));
        this.allGroups = groups.map(group => ({
          ...group,
          selected: !!this.props.value.groups.find(g => g.id === group.id)
        }));
        this.publishGlobally = this.props.value.publish_globally;
        this.applyFilter(this.searchFilter.value);
        this.loading.dismiss();
      });

    this.searchFilter
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(searchText => this.applyFilter(searchText));
  }

  private applyFilter(searchText: string) {
    const term = searchText.toLowerCase();
    this.settlements = this.allSettlements.filter(settlement =>
      settlement.title.toLowerCase().includes(term)
    );
    this.groups = this.allGroups.filter(group =>
      group.title.toLowerCase().includes(term)
    );
  }

  private fetchSettlements() {
    this.staffOrAdmin$.subscribe(staffOrAdmin => {
      if (staffOrAdmin) {
        this.settlements$ = this.settlementService.data$.pipe(
          skipWhile(r => r.state === 'loading'),
          map(r => (r?.data?.items || []) as Settlement[]),
          first()
        );
        this.settlementService.fetch({ fetch_all: true });
      } else {
        this.settlements$ = this.userStore.pipe(
          select(getCurrentUser),
          first(),
          map(u => [u.settlement])
        );
      }
    });
  }

  private fetchGroups() {
    this.groups$ = this.groupService.myGroups$.pipe(
      skipWhile(r => r.state === 'loading'),
      map(r => (r.data || []) as Group[])
    );
    this.groupService.fetchMyGroups();
  }

  toggleSettlement(settlement: ScopeOption) {
    this.publishGlobally = false;
    settlement.selected = !settlement.selected;
  }

  toggleGroup(group: ScopeOption) {
    this.publishGlobally = false;
    group.selected = !group.selected;
  }

  selectAll() {
    if (this.publishGlobally) return;

    this.publishGlobally = true;
    this.allGroups.forEach(group => (group.selected = false));
    this.allSettlements.forEach(settlement => (settlement.selected = false));
  }
}
